import useGetCurrentCustomerData from 'hooks/Authentication/useGetCurrentCustomerData';
import { useCallback } from 'react';
import type { SubscriptionPlan } from 'types/subscriptions.types';
import type { SubscriptionPlanNameEnum } from 'types/users.types';
import { isSubscriptionPaidPlan } from 'utils/subscriptions.utils';
import useGroupSubscriptionPlans from './useGroupSubscriptionPlans';
import useRedirectToCheckoutSession from './useRedirectToCheckoutSession';
import useRedirectToCustomerBillingPortal from './useRedirectToCustomerBillingPortal';
import useSubscriptionPlan from './useSubscriptionPlan';

const useStripeSubscription = () => {
  const subscriptionPlan = useSubscriptionPlan();
  const { planInternalName, fromPlatform } = subscriptionPlan || {};
  const { data: user } = useGetCurrentCustomerData();
  const {
    mutate: redirectToBilingPortal,
    isLoading: isBillingPortalLoading,
    isError: isBillingPortalError,
  } = useRedirectToCustomerBillingPortal();
  const {
    mutate: redirectToCheckoutSession,
    isLoading: isCheckoutSessionLoading,
    isError: isCheckoutSessionError,
  } = useRedirectToCheckoutSession(user);
  const { data: subscriptionPlans = [] } = useGroupSubscriptionPlans(
    subscriptionPlan?.planInternalName,
  );

  const subscribe = useCallback(
    async (targetPlan: any, couponId?: string) => {
      let plan = targetPlan;

      if (typeof targetPlan === 'string') {
        plan = subscriptionPlans?.find(
          (plan: SubscriptionPlan) => plan.nickname === targetPlan,
        );
      }

      if (
        (fromPlatform === 'stripe' || fromPlatform === 'stripe-otp') &&
        isSubscriptionPaidPlan(planInternalName)
      ) {
        return redirectToBilingPortal();
      }

      return redirectToCheckoutSession({
        planId: plan?.id as string,
        nickname: plan?.nickname as SubscriptionPlanNameEnum,
        promocode: couponId,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [subscriptionPlans, fromPlatform, planInternalName],
  );

  return {
    subscribe,
    isLoading: isCheckoutSessionLoading || isBillingPortalLoading,
    isError: isBillingPortalError || isCheckoutSessionError,
  };
};

export default useStripeSubscription;
